import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { RequiredObjectSchema } from 'yup/lib/object'

import { useAdaptConnectorNaming } from '../../lib/adaptConnectorNaming'
import { isChargingConnector } from '../../lib/findChargingConnector'
import isAura from '../../lib/isAura'
import { CHARGEPOINT_MIN_CURRENT, getInstallationCurrentLimit } from '../domain/electrical-settings'
import { ChargePoint, ChargePointImport, Connector } from '../redux/api/web-api'
import { MAX_NAME_LENGTH } from '../constants/InputsLength'

import useText from './useText'

export interface ICapiFormInput {
    name: string | null | undefined
    description?: string | null
    nfcEnabled: boolean
    supplyCurrent: number
    installationPhaseMode: string
    connectorPhaseRotation: string
    connectorType_1: string
    installationPhaseMode_1: string
    installationPhase_1: string
    ic_1?: number
    uc_1?: number
    lbc_1?: number
    lbOfflinePhase_1?: string
    connectorType_2?: string
    installationPhaseMode_2?: string
    installationPhase_2?: string
    ic_2?: number
    uc_2?: number
    lbc_2?: number
    lbOfflinePhase_2?: string
}

export const capiChargerDefaultValues = (chargePoint: ChargePoint | ChargePointImport): ICapiFormInput => {
    let retVal: ICapiFormInput = {
        name: chargePoint.name,
        nfcEnabled: chargePoint.nfcEnabled ?? false,
        supplyCurrent: chargePoint.supplyCurrent,
    }

    chargePoint.connectors?.forEach((connector) => {
        retVal = {
            ...retVal,
            [`connectorType_${connector.connectorId}`]: connector.connectorType,
            [`installationPhaseMode_${connector.connectorId}`]: connector.installationPhaseMode,
            [`installationPhase_${connector.connectorId}`]: connector.installationPhase,
            [`ic_${connector.connectorId}`]: connector.installationCurrent,
            [`uc_${connector.connectorId}`]: connector.userCurrent,
            [`lbc_${connector.connectorId}`]: connector.loadBalancerOfflineCurrent,
            [`lbOfflinePhase_${connector.connectorId}`]: connector.loadBalancerOfflinePhase,
        }
    })

    return retVal
}

const useUserCurrentValidationSchema = () => {
    const text = useText()

    return (chargePoint: ChargePoint, connectorId: number, defaultValues: ICapiFormInput) =>
        yup
            .number()
            .min(
                chargePoint.connectors?.find((connector) => connector.connectorId === connectorId)?.minCurrent ??
                    CHARGEPOINT_MIN_CURRENT,
                text('msgUserCurrentLimitLowerThanMin')
            )
            .test(
                'less-than-installation-current',
                text('msgUserCurrentMoreThanInstallationCurrent'),
                function (value, form) {
                    if (!value || isNaN(value)) return true

                    const maxValKey = `ic_${connectorId}`

                    const maxVal = form.parent[maxValKey] ? form.parent[maxValKey] : defaultValues[maxValKey]

                    return value <= maxVal
                }
            )
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable()
            .optional()
}

export const useLoadbalancerOfflineCurrentSchema = () => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    return (connectorId?: number, connectorName?: string, minCurrent: number = CHARGEPOINT_MIN_CURRENT) =>
        yup
            .number()
            .required(text('msgLBOfflineCurrent'))
            .min(
                CHARGEPOINT_MIN_CURRENT,
                text('lblInstallationCurrentOfflineValidation', {
                    connectorType: adaptConnectorNaming(connectorName ?? ''),
                    minCurrent: minCurrent,
                })
            )
            .transform((value) => (isNaN(value) ? undefined : value))
            .test('less-than-installation-current', function (value, form) {
                if (value) {
                    if (form.parent[`ic_${connectorId}`] < value) {
                        return this.createError({
                            path: this.path,
                            message: text('lblInstallationCurrentOfflineValidationMax', {
                                connectorType: adaptConnectorNaming(connectorName),
                                installationCurrent: form.parent[`ic_${connectorId}`],
                            }),
                        })
                    }
                }

                return true
            })
}

export const useLoadbalancerOfflinePhaseSchema = () => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    return (connectorName: string | null | undefined) =>
        yup.string().required(
            text('lblLoadBalancerOfflinePhaseValidationConnector', {
                name: adaptConnectorNaming(connectorName ?? ''),
            })
        )
}

export const useCapiFormValidationSchema = () => {
    const text = useText()
    const userCurrentValidationSchema = useUserCurrentValidationSchema()
    const loadbalancerOfflinePhaseSchema = useLoadbalancerOfflinePhaseSchema()
    const loadbalancerOfflineCurrentSchema = useLoadbalancerOfflineCurrentSchema()

    return (chargePoint: ChargePoint, defaultValues: ICapiFormInput) => {
        const allConnectorsMaxCurrent = chargePoint.connectors
            ?.filter(isChargingConnector)
            .reduce((acc: number, curr: Connector) => acc + Number(curr.factoryCurrent), 0)

        const capiDefaultValues = defaultValues ?? capiChargerDefaultValues(chargePoint)

        const supplyCurrentValidationFactory = () =>
            yup
                .number()
                .positive()
                .integer()
                .required(text('msgSupplyCurrentRequired'))
                .max(
                    allConnectorsMaxCurrent,
                    text('msgSupplyCurrentMoreThanMaxCurrent', {
                        factoryCurrent: allConnectorsMaxCurrent,
                    })
                )
                .min(
                    CHARGEPOINT_MIN_CURRENT,
                    text('msgValueCannotBeBelowChargerMinCurrent', { minCurrent: CHARGEPOINT_MIN_CURRENT })
                )
                .transform((value) => (isNaN(value) ? undefined : value))

        const installationCurrentValidationFactory = (connectorId: number) =>
            yup
                .number()
                .positive()
                .integer()
                .required(text('msgInstallationCurrentRequired'))
                .min(
                    chargePoint.connectors?.find((connector) => connector.connectorId === connectorId)?.minCurrent ??
                        CHARGEPOINT_MIN_CURRENT,
                    text('msgInstallationCurrentLimitLowerThanMin')
                )
                .test('less-than-maxCurrent', function (value, form) {
                    const factoryCurrent = chargePoint.connectors?.find(
                        (connector) => connector?.connectorId === connectorId
                    )?.factoryCurrent

                    const maxCurrent = getInstallationCurrentLimit(form.parent.supplyCurrent, factoryCurrent)
                    if (value > maxCurrent) {
                        return this.createError({
                            path: this.path,
                            message: text('msgInstallationCurrentMoreThanConnectorMaxCurrent', {
                                maxCurrent: maxCurrent,
                            }),
                        })
                    }
                    return true
                })
                .transform((value) => (isNaN(value) ? undefined : value))

        return yup
            .object({
                name: yup.string().required().max(MAX_NAME_LENGTH, text('lblNameError')),
                supplyCurrent: supplyCurrentValidationFactory(),
                ic_1: installationCurrentValidationFactory(1),
                uc_1: userCurrentValidationSchema(chargePoint, 1, capiDefaultValues),
                ...(isAura(chargePoint) && {
                    ic_2: installationCurrentValidationFactory(2),
                    uc_2: userCurrentValidationSchema(chargePoint, 2, capiDefaultValues),
                }),
                ...(chargePoint.loadBalancerGroupId &&
                    chargePoint.connectors && {
                        lbc_1: loadbalancerOfflineCurrentSchema(
                            1,
                            chargePoint.connectors[0]?.name ?? '',
                            chargePoint.connectors?.find((connector) => connector.connectorId === 1)?.minCurrent
                        ),
                        lbOfflinePhase_1: loadbalancerOfflinePhaseSchema(chargePoint.connectors[0]?.name),
                        ...(isAura(chargePoint) && {
                            lbc_2: loadbalancerOfflineCurrentSchema(
                                2,
                                chargePoint.connectors[1]?.name ?? '',
                                chargePoint.connectors?.find((connector) => connector.connectorId === 2)?.minCurrent
                            ),
                            lbOfflinePhase_2: loadbalancerOfflinePhaseSchema(chargePoint.connectors[1]?.name),
                        }),
                    }),
            })
            .required()
    }
}

const useCapiFormValidation = (chargePoint: ChargePoint) => {
    const defaultValues = capiChargerDefaultValues(chargePoint)
    const capiValidationSchema = useCapiFormValidationSchema()

    return useCreateForm(capiValidationSchema(chargePoint, defaultValues), defaultValues, chargePoint)
}

export const useCapiUserCurrentValidation = (chargePoint: ChargePoint) => {
    const defaultValues = capiChargerDefaultValues(chargePoint)
    const userCurrentValidationSchema = useUserCurrentValidationSchema()

    const schema = yup
        .object({
            uc_1: userCurrentValidationSchema(chargePoint, 1, defaultValues),
            ...(isAura(chargePoint) && {
                uc_2: userCurrentValidationSchema(chargePoint, 2, defaultValues),
            }),
        })
        .required()

    return useCreateForm(schema, defaultValues, chargePoint, 'onSubmit')
}

const useCreateForm = (
    validationSchema: RequiredObjectSchema,
    defaultValues: ICapiFormInput,
    chargePoint: ChargePoint | ChargePointImport,
    mode: 'onChange' | 'onSubmit' = 'onChange'
) =>
    useForm<ICapiFormInput>({
        mode,
        reValidateMode: 'onChange',
        delayError: 500,
        defaultValues: {
            ...defaultValues,
            lbOfflinePhase_1:
                chargePoint.connectors && chargePoint.connectors[0].loadBalancerOfflinePhase
                    ? chargePoint.connectors[0].loadBalancerOfflinePhase
                    : 'L1',
            lbOfflinePhase_2:
                chargePoint.connectors && chargePoint.connectors[0].loadBalancerOfflinePhase
                    ? chargePoint.connectors[0].loadBalancerOfflinePhase
                    : 'L1',
        },
        resolver: yupResolver(validationSchema),
    })

export default useCapiFormValidation
